/**
 * Sources Hooks
 * Jotai atoms and hooks for source management
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { useEffect, useMemo } from 'react';
import { toast } from '@/components/ui/sonner';
import { sourceService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { useWebSocket } from './use-websocket';
import type { CreateSourceDto, UpdateSourceDto, SourcesListQueryParams } from '@/types';

// Query atoms

export const sourcesFilteredQueryAtom = (params?: { categrizationId?: string }) =>
  atomWithQuery((get) => ({
    queryKey: ['sources', params ?? {}, buScopeKey(get)],
    queryFn: () => sourceService.getSources({ ...params, ...buScopeParams(get) })
  }));

export const sourcesPaginatedQueryAtom = (params: SourcesListQueryParams) =>
  atomWithQuery((get) => ({
    queryKey: ['sources', 'paginated', params, buScopeKey(get)],
    queryFn: () => sourceService.getSourcesPaginated({ ...params, ...buScopeParams(get) })
  }));

export const mySourcesQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sources', 'my-sources', buScopeKey(get)],
  queryFn: () => sourceService.getMySources(buScopeParams(get))
}));

// The count above the table has to agree with the table, so stats carry the same scope.
export const sourceStatsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sources', 'stats', buScopeKey(get)],
  queryFn: () => sourceService.getStats(buScopeParams(get))
}));

export const sourceByIdQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: ['sources', id],
    queryFn: () => sourceService.getSourceById(id),
    enabled: !!id
  }));

export const sourceContentQueryAtom = (id: string, enabled: boolean) =>
  atomWithQuery(() => ({
    queryKey: ['sources', id, 'content'],
    queryFn: () => sourceService.getSourceContent(id),
    enabled: !!id && enabled
  }));

export const sourceExportPreviewQueryAtom = (id: string, enabled: boolean) =>
  atomWithQuery(() => ({
    queryKey: ['sources', id, 'export-preview'],
    queryFn: () => sourceService.getExportPreview(id),
    enabled: !!id && enabled
  }));

// Mutation atoms
const createSourceMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['sources', 'create'],
  mutationFn: (data: CreateSourceDto) => sourceService.createSource(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['sources']);
  }
}));

const updateSourceMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sources', 'update'],
  mutationFn: ({ id, data }: { id: string; data: UpdateSourceDto }) => sourceService.updateSource(id, data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['sources']);
    invalidateQueries(['sources', variables.id]);
  }
}));

const deleteSourceMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sources', 'delete'],
  mutationFn: (id: string) => sourceService.deleteSource(id),
  onSuccess: () => {
    invalidateQueries(['sources']);
    // Deleting a Source can empty out its category — refetch the sidebar tree so an
    // now-empty subcategory disappears immediately instead of only on next reload.
    invalidateQueries(['sops', 'categorization-tree']);
  }
}));

const resyncSourceMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sources', 'resync'],
  mutationFn: (id: string) => sourceService.resyncSource(id),
  onSuccess: () => {
    invalidateQueries(['sources']);
  }
}));

export const updateSourceCategorizationMutationAtom = atomWithMutation(() => ({
  mutationKey: ['sources', 'update-categorization'],
  mutationFn: ({ id, category, subcategory }: { id: string; category: string; subcategory: string }) =>
    sourceService.updateCategorization(id, category, subcategory),
  onSuccess: () => {
    invalidateQueries(['sources']);
    invalidateQueries(['pulse', 'subcategory-counts']);
    invalidateQueries(['sops', 'categorization-tree']);
  }
}));

// Hooks
export function useSources(params?: { categrizationId?: string }) {
  const filterKey = JSON.stringify(params ?? {});
  const atom = useMemo(() => sourcesFilteredQueryAtom(params), [filterKey]);
  const [sourcesQuery] = useAtom(atom);
  return sourcesQuery;
}

export function useSourcesPaginated(params: SourcesListQueryParams) {
  const filterKey = JSON.stringify(params);
  const atom = useMemo(() => sourcesPaginatedQueryAtom(params), [filterKey]);
  const [sourcesQuery] = useAtom(atom);
  return sourcesQuery;
}

export function useMySources() {
  const [mySourcesQuery] = useAtom(mySourcesQueryAtom);
  return mySourcesQuery;
}

export function useSourceStats() {
  const [statsQuery] = useAtom(sourceStatsQueryAtom);
  return statsQuery;
}

export function useSourceById(id: string) {
  const atom = useMemo(() => sourceByIdQueryAtom(id), [id]);
  const [query] = useAtom(atom);
  return query;
}

export function useSourceContent(id: string, enabled: boolean) {
  const atom = useMemo(() => sourceContentQueryAtom(id, enabled), [id, enabled]);
  const [query] = useAtom(atom);
  return query;
}

export function useSourceExportPreview(id: string, enabled: boolean) {
  const atom = useMemo(() => sourceExportPreviewQueryAtom(id, enabled), [id, enabled]);
  const [query] = useAtom(atom);
  return query;
}

export function useCreateSource() {
  const [mutation] = useAtom(createSourceMutationAtom);
  return mutation;
}

export function useUpdateSource() {
  const [mutation] = useAtom(updateSourceMutationAtom);
  return mutation;
}

export function useDeleteSource() {
  const [mutation] = useAtom(deleteSourceMutationAtom);
  return mutation;
}

export function useResyncSource() {
  const [mutation] = useAtom(resyncSourceMutationAtom);
  return mutation;
}

const autoCategorizeSourcesMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['sources', 'auto-categorize'],
  mutationFn: () => sourceService.autoCategorize(buScopeParams(get)),
  onSuccess: ({ scanned, categorized }: { scanned: number; categorized: number }) => {
    // What was left keeps its reason: the ⓘ next to it says why on hover.
    if (scanned === 0) toast.message('Everything already has a category');
    else if (categorized === scanned) toast.success(`Filed ${categorized} of ${scanned} sources under a category`);
    else toast.message(`Filed ${categorized} of ${scanned} sources`, { description: 'Hover the ⓘ on the rest to see why.' });
    invalidateQueries(['sources']);
    invalidateQueries(['sops', 'categorization-tree']);
  },
  onError: () => toast.error('Could not categorize the sources')
}));

export function useAutoCategorizeSources() {
  const [mutation] = useAtom(autoCategorizeSourcesMutationAtom);
  return mutation;
}

export function useUpdateSourceCategorization() {
  const [mutation] = useAtom(updateSourceCategorizationMutationAtom);
  return mutation;
}

/**
 * Hook to listen for real-time source status updates via WebSocket
 * Automatically invalidates queries and shows toast notifications
 */
export function useSourceStatusUpdates(): void {
  const { setSourceStatusUpdateHandler, setSourceStatusErrorHandler, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    // Handler for successful status updates
    const handleStatusUpdate = (data: {
      sourceId: string;
      status: 'SUCCESS' | 'FAILED' | 'INPROGRESS';
      accountId: string;
      message: string;
      timestamp: number;
    }) => {
      console.log('[Sources] Source status updated:', data);

      // Invalidate queries to trigger refetch
      invalidateQueries(['sources']);
      invalidateQueries(['sources', data.sourceId]);
      invalidateQueries(['sources', 'stats']);

      // Show toast notification
      if (data.status === 'SUCCESS') {
        toast.success('Source synced successfully', {
          description: data.message
        });
      } else if (data.status === 'FAILED') {
        toast.error('Source sync failed', {
          description: data.message
        });
      } else if (data.status === 'INPROGRESS') {
        toast.info('Source is syncing', {
          description: data.message
        });
      }
    };

    // Handler for status update errors
    const handleStatusError = (data: { sourceId: string; error: string; timestamp: number }) => {
      console.error('[Sources] Source status update error:', data);

      // Show error notification
      toast.error('Failed to update source status', {
        description: data.error
      });

      // Refetch to ensure consistency
      invalidateQueries(['sources']);
    };

    // Register handlers
    setSourceStatusUpdateHandler(handleStatusUpdate);
    setSourceStatusErrorHandler(handleStatusError);

    // Cleanup handlers on unmount
    return () => {
      setSourceStatusUpdateHandler(() => {});
      setSourceStatusErrorHandler(() => {});
    };
  }, [isConnected, setSourceStatusUpdateHandler, setSourceStatusErrorHandler]);
}
